import { getRenderingRef, getElement, forceUpdate } from '@stencil/core';
import { debounce } from '@kurrent-ui/utils';
import { themeCore } from './themeCore.mjs';

/** Theme info and control. */
class ThemeControl {
    constructor() {
        this.interestedElements = new Set();
        this.select = themeCore.select;
        this.updateTheme = themeCore.updateTheme;
        this.autoThemeName = themeCore.autoThemeName;
        this.onThemeChange = themeCore.onThemeChange;
        this.isHighContrast = () => this.contrast === 'high';
        this.isLowContrast = () => this.contrast === 'low';
        this.isLight = () => this.shade === 'light';
        this.isDark = () => this.shade === 'dark';
        this.registerInterest = () => {
            const el = getRenderingRef();
            if (el)
                this.interestedElements.add(el);
            this.scheduleCleanup();
        };
        this.scheduleCleanup = debounce(() => {
            var _a;
            for (const element of this.interestedElements) {
                if ((_a = getElement(element)) === null || _a === void 0 ? void 0 : _a.isConnected)
                    continue;
                this.interestedElements.delete(element);
            }
        }, 2000);
        this.refreshElements = () => {
            for (const el of Array.from(this.interestedElements)) {
                // force update returns false if it fails but is typed wrong
                const successfullyUpdated = forceUpdate(el);
                if (!successfullyUpdated) {
                    this.interestedElements.delete(el);
                }
            }
            this.scheduleCleanup();
        };
        themeCore.onThemeChange(() => {
            this.refreshElements();
        });
    }
    get selected() {
        return themeCore.selected;
    }
    get name() {
        this.registerInterest();
        return themeCore.activeTheme.name;
    }
    get colors() {
        this.registerInterest();
        return themeCore.activeTheme.scheme;
    }
    get contrast() {
        this.registerInterest();
        return themeCore.activeTheme.meta.contrast;
    }
    get shade() {
        this.registerInterest();
        return themeCore.activeTheme.meta.shade;
    }
}
/**
 * The global theme object, for getting info about and controlling the currently active theme.
 * @usage ./theme.usage.md
 */
const theme = new ThemeControl();

export { ThemeControl, theme };
